import React from 'react';
import './NetworkModal.css';

interface NetworkModalProps {
  reef: any;
  reefSurroundings: any;
  onClose: () => void;
}

interface NetworkNode {
  id: string;
  type: 'reef' | 'pod' | 'farm';
  name: string;
  x: number;
  y: number;
  distance?: number;
}

interface NetworkEdge {
  from: string;
  to: string;
  distance: number;
}

export const NetworkModal: React.FC<NetworkModalProps> = ({ reef, reefSurroundings, onClose }) => {
  const modalSize = 600;
  const center = { x: modalSize / 2, y: modalSize / 2 };
  const maxDistance = Math.max(
    ...(reefSurroundings.nearby_pods?.map((p: any) => p.distance) || [0]),
    ...(reefSurroundings.nearby_reefs?.map((r: any) => r.distance) || [0]),
    ...(reefSurroundings.nearby_farms?.map((f: any) => f.distance) || [0]),
    reef.detection_range
  );

  // Create nodes
  const nodes: NetworkNode[] = [
    {
      id: `reef-${reef.id}`,
      type: 'reef',
      name: reef.name,
      x: center.x,
      y: center.y
    }
  ];

  // Add nearby pods
  (reefSurroundings.nearby_pods || []).forEach((pod: any, index: number) => {
    const angle = (index * 360 / reefSurroundings.nearby_pods.length) * Math.PI / 180;
    const radius = (pod.distance / maxDistance) * (modalSize / 3);
    nodes.push({
      id: `pod-${pod.id}`,
      type: 'pod',
      name: pod.name,
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
      distance: pod.distance
    });
  });

  // Add nearby reefs
  const podCount = reefSurroundings.nearby_pods?.length || 0;
  (reefSurroundings.nearby_reefs || []).forEach((otherReef: any, index: number) => {
    const angle = ((index + podCount) * 360 / ((reefSurroundings.nearby_reefs?.length || 0) + podCount)) * Math.PI / 180;
    const radius = (otherReef.distance / maxDistance) * (modalSize / 3);
    nodes.push({
      id: `reef-${otherReef.id}`,
      type: 'reef',
      name: otherReef.name,
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
      distance: otherReef.distance
    });
  });

  // Add nearby farms (as background info)
  const existingCount = podCount + (reefSurroundings.nearby_reefs?.length || 0);
  (reefSurroundings.nearby_farms || []).forEach((farm: any, index: number) => {
    const angle = ((index + existingCount) * 360 / (existingCount + (reefSurroundings.nearby_farms?.length || 0))) * Math.PI / 180;
    const radius = (farm.distance / maxDistance) * (modalSize / 4);
    nodes.push({
      id: `farm-${farm.id}`,
      type: 'farm',
      name: farm.name,
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
      distance: farm.distance
    });
  });

  // Create edges from central reef to all other nodes
  const edges: NetworkEdge[] = nodes
    .filter(node => node.id !== `reef-${reef.id}`)
    .map(node => ({
      from: `reef-${reef.id}`,
      to: node.id,
      distance: node.distance || 0
    }));

  return (
    <div className="network-modal-overlay" onClick={onClose}>
      <div className="network-modal" onClick={e => e.stopPropagation()}>
        <div className="network-header">
          <h3>Network Graph - {reef.name}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="network-content">
          <svg width={modalSize} height={modalSize} viewBox={`0 0 ${modalSize} ${modalSize}`}>
            {/* Detection range circle */}
            <circle
              cx={center.x}
              cy={center.y}
              r={(reef.detection_range / maxDistance) * (modalSize / 3)}
              fill="none"
              stroke="#ff4444"
              strokeWidth="2"
              strokeDasharray="5,5"
              opacity="0.5"
            />
            
            {/* Edges */}
            {edges.map((edge, index) => {
              const fromNode = nodes.find(n => n.id === edge.from);
              const toNode = nodes.find(n => n.id === edge.to);
              if (!fromNode || !toNode) return null;
              
              const midX = (fromNode.x + toNode.x) / 2;
              const midY = (fromNode.y + toNode.y) / 2;
              
              return (
                <g key={index}>
                  <line
                    x1={fromNode.x}
                    y1={fromNode.y}
                    x2={toNode.x}
                    y2={toNode.y}
                    stroke="#666"
                    strokeWidth="1"
                  />
                  <text
                    x={midX}
                    y={midY}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#333"
                    className="distance-label"
                  >
                    {Math.round(edge.distance)}m
                  </text>
                </g>
              );
            })}
            
            {/* Nodes */}
            {nodes.map(node => (
              <g key={node.id}>
                {node.type === 'reef' && (
                  <rect
                    x={node.x - 8}
                    y={node.y - 8}
                    width="16"
                    height="16"
                    fill={node.id === `reef-${reef.id}` ? "#ff0000" : "#ff6666"}
                    stroke="#cc0000"
                    strokeWidth="2"
                  />
                )}
                {node.type === 'pod' && (
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r="8"
                    fill="#90EE90"
                    stroke="#008000"
                    strokeWidth="2"
                  />
                )}
                {node.type === 'farm' && (
                  <polygon
                    points={`${node.x},${node.y-8} ${node.x+8},${node.y+8} ${node.x-8},${node.y+8}`}
                    fill="#87CEEB"
                    stroke="#4682B4"
                    strokeWidth="2"
                  />
                )}
                <text
                  x={node.x}
                  y={node.y + 20}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#333"
                  className="node-label"
                >
                  {node.name}
                </text>
              </g>
            ))}
          </svg>
          
          <div className="network-legend">
            <h4>Legend:</h4>
            <div className="legend-item">
              <div className="legend-icon reef-icon"></div>
              <span>Artificial Reefs</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon pod-icon"></div>
              <span>Mussel Pods</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon farm-icon"></div>
              <span>Farms</span>
            </div>
            <div className="legend-item">
              <div className="legend-line"></div>
              <span>Distance (meters)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
